import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';

const LoginModal = ({ isOpen, onClose }) => {
    const { login } = useAuth();

    const [formData, setFormData] = useState({ username: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.username.trim()) {
            setUsernameError('Username is required');
            return;
        }
        if (!formData.password.trim()) {
            setPasswordError('Password is required');
            return;
        }

        setLoading(true);
        setError('');
        setUsernameError('');
        setPasswordError('');

        try {
            const response = await fetch(`${API_BASE_URL}/api/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            const result = await response.json();

            if (!response.ok) {
                if (result?.errors) {
                    setUsernameError(result.errors.username?.[0] || '');
                    setPasswordError(result.errors.password?.[0] || '');
                } else {
                    setError(result.message || 'Login failed. Please try again.');
                }
                setLoading(false);
                return;
            }

            if (!result.token || !result.name || !result.role) {
                setError('Invalid login response from server.');
                setLoading(false);
                return;
            }

            // Use AuthContext login instead of localStorage directly
            await login({
                token: result.token,
                refreshToken: result.refreshToken || '',
                name: result.name,
                role: result.role,
                username: formData.username,
            });

            onClose();

        } catch (err) {
            console.error("Login error:", err);
            setError('An unexpected error occurred. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-white w-full max-w-xl p-8 border border-[#144E73] rounded-lg shadow-lg relative">
                <button
                    onClick={onClose}
                    className="absolute top-2 right-3 text-gray-500 hover:text-red-600 text-xl"
                    aria-label="Close login modal"
                >
                    &times;
                </button>

                <div className="flex flex-col items-center mb-4">
                    <img src="/logo.png" alt="Logo" className="h-12 mb-2" />
                    <hr className="w-full border-gray-300 mb-4" />
                </div>

                <h2 className="text-2xl font-bold text-center text-[#144E73] mb-6">Login</h2>

                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    <div>
                        <label className="block font-medium mb-1" htmlFor="username">Username</label>
                        <input
                            id="username"
                            type="text"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 ${
                                usernameError ? 'border-red-500 focus:ring-red-300' : 'focus:ring-[#F29E38]'
                            }`}
                        />
                        {usernameError && <p className="text-red-600 text-sm mt-1">{usernameError}</p>}
                    </div>

                    <div>
                        <label className="block font-medium mb-1" htmlFor="password">Password</label>
                        <input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 ${
                                passwordError ? 'border-red-500 focus:ring-red-300' : 'focus:ring-[#F29E38]'
                            }`}
                        />
                        {passwordError && <p className="text-red-600 text-sm mt-1">{passwordError}</p>}

                        <label className="inline-flex items-center mt-2 cursor-pointer">
                            <input
                                type="checkbox"
                                className="form-checkbox text-[#F29E38]"
                                checked={showPassword}
                                onChange={() => setShowPassword(prev => !prev)}
                            />
                            <span className="ml-2 text-sm text-gray-700 select-none">Show Password</span>
                        </label>
                    </div>

                    {error && (
                        <p className="text-red-600 text-sm font-medium bg-red-100 p-2 rounded">
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#F29E38] text-[#023059] py-2 rounded-full font-semibold hover:bg-opacity-90 transition disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default LoginModal;
